/** Employee documents (contracts, certificates). */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { FileUpload } from "@primereact/ui/fileupload";
import { InputText as Input } from "@primereact/ui/inputtext";
import { Select } from "@primereact/ui/select";
import { apiDelete, apiGet, apiUpload } from "../../lib/api.js";
import {
  ConfirmButton,
  ErrorPanel,
  LoadingPanel,
  MutationError,
  Section,
} from "../shared.js";
import type { DocumentCategoryData, EmployeeDocumentData } from "./types.js";

export function EmployeeDocuments({
  userId,
  canManage,
}: {
  userId: string;
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [showUpload, setShowUpload] = useState(false);
  const documents = useQuery({
    queryKey: ["employee-documents", userId],
    queryFn: () =>
      apiGet<{
        data: EmployeeDocumentData[];
      }>(`/employee-documents/users/${userId}`).then(
        (response) => response.data,
      ),
  });
  const categories = useQuery({
    queryKey: ["employee-document-categories"],
    queryFn: () =>
      apiGet<{
        data: DocumentCategoryData[];
      }>("/employee-documents/categories").then((response) => response.data),
    enabled: canManage,
  });
  const remove = useMutation({
    mutationFn: (id: string) => apiDelete(`/employee-documents/${id}`),
    onSuccess: async () =>
      client.invalidateQueries({
        queryKey: ["employee-documents", userId],
      }),
  });
  const grouped = useMemo(
    () =>
      Object.entries(
        (documents.data ?? []).reduce<Record<string, EmployeeDocumentData[]>>(
          (result, document) => {
            (result[document.category.name] ??= []).push(document);
            return result;
          },
          {},
        ),
      ),
    [documents.data],
  );
  return (
    <>
      <div className="mb-4 flex justify-end">
        {canManage ? (
          <Button onClick={() => setShowUpload((value) => !value)}>
            {t("documents.upload")}
          </Button>
        ) : null}
      </div>
      {showUpload ? (
        <DocumentUpload
          userId={userId}
          categories={categories.data ?? []}
          onDone={async () => {
            setShowUpload(false);
            await client.invalidateQueries({
              queryKey: ["employee-documents", userId],
            });
          }}
        />
      ) : null}
      {documents.isLoading ? (
        <LoadingPanel />
      ) : documents.error ? (
        <ErrorPanel error={documents.error} />
      ) : grouped.length === 0 ? (
        <Section>
          <p className="text-sm text-app-muted">{t("documents.empty")}</p>
        </Section>
      ) : (
        <div className="space-y-5">
          {grouped.map(([category, rows]) => (
            <Section key={category} title={category}>
              <div className="divide-y divide-app-border">
                {rows.map((document) => (
                  <div
                    key={document.id}
                    className="flex flex-wrap items-center justify-between gap-3 py-3"
                  >
                    <div>
                      <a
                        className="font-medium text-brand hover:underline"
                        href={`/api/v1/employee-documents/${document.id}/download`}
                      >
                        {document.title}
                      </a>
                      <p className="mt-1 text-xs text-app-muted">
                        {document.fileName} ·{" "}
                        {(document.sizeBytes / 1024).toFixed(0)} KB ·{" "}
                        {new Date(document.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-[var(--p-content-hover-background)] px-2 py-1 text-xs text-app-muted">
                        {document.visibility}
                      </span>
                      {canManage ? (
                        <ConfirmButton
                          onConfirm={() => remove.mutate(document.id)}
                          confirmMessage={t("documents.deleteConfirm")}
                        >
                          {t("common.delete")}
                        </ConfirmButton>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          ))}
        </div>
      )}
    </>
  );
}
function DocumentUpload({
  userId,
  categories,
  onDone,
}: {
  userId: string;
  categories: DocumentCategoryData[];
  onDone: () => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [categoryId, setCategoryId] = useState(categories[0]?.id ?? "");
  const [visibility, setVisibility] = useState("employee");
  const upload = useMutation({
    mutationFn: async () => {
      if (!file) return;
      const form = new FormData();
      form.append("file", file);
      const query = new URLSearchParams({
        title: title || file.name,
        categoryId,
        visibility,
      });
      await apiUpload(
        `/employee-documents/users/${userId}/upload?${query.toString()}`,
        form,
      );
    },
    onSuccess: onDone,
  });
  return (
    <Section className="mb-5" title={t("documents.newUpload")}>
      <form
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault();
          upload.mutate();
        }}
      >
        <Field label={t("documents.chooseFile")} required>
          <FileUpload.Root
            name="file"
            auto={false}
            customUpload
            onSelect={(event: { files: File[] }) => {
              const next = event.files?.[0] ?? null;
              setFile(next);
              if (next && !title) {
                setTitle(next.name.replace(/\.[^.]+$/, ""));
              }
            }}
          >
            <FileUpload.Trigger>
              <i className="pi pi-upload"></i>
              <span>{t("documents.chooseFile")}</span>
            </FileUpload.Trigger>
            <FileUpload.Content>
              <FileUpload.ItemGroup></FileUpload.ItemGroup>
            </FileUpload.Content>
          </FileUpload.Root>
        </Field>
        <Field label={t("common.title")} required>
          <Input
            required
            value={title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setTitle(e.target.value)
            }
          />
        </Field>
        <Field label={t("documents.category")} required>
          <Select.Root
            value={categoryId}
            onValueChange={(e: { value: unknown }) =>
              setCategoryId(e.value as string)
            }
            fluid
            options={[
              ...categories.map((category) => ({
                value: category.id,
                label: category.name,
              })),
            ]}
            optionLabel="label"
            optionValue="value"
          >
            <Select.Trigger type="button">
              <Select.Value />
              <Select.Indicator className="pi pi-chevron-down" />
            </Select.Trigger>
            <Select.Portal>
              <Select.Positioner>
                <Select.Popup>
                  <Select.List />
                </Select.Popup>
              </Select.Positioner>
            </Select.Portal>
          </Select.Root>
        </Field>
        <Field label={t("documents.visibility")}>
          <Select.Root
            value={visibility}
            onValueChange={(e: { value: unknown }) =>
              setVisibility(e.value as string)
            }
            fluid
            options={[
              {
                value: "employee",
                label: t("documents.employeeVisible"),
              },
              {
                value: "management",
                label: t("documents.managementOnly"),
              },
            ]}
            optionLabel="label"
            optionValue="value"
          >
            <Select.Trigger type="button">
              <Select.Value />
              <Select.Indicator className="pi pi-chevron-down" />
            </Select.Trigger>
            <Select.Portal>
              <Select.Positioner>
                <Select.Popup>
                  <Select.List />
                </Select.Popup>
              </Select.Positioner>
            </Select.Portal>
          </Select.Root>
        </Field>
        <MutationError error={upload.error} />
        <div className="sm:col-span-2">
          <Button
            type="submit"
            disabled={!file || !categoryId}
            loading={upload.isPending || undefined}
          >
            {t("documents.upload")}
          </Button>
        </div>
      </form>
    </Section>
  );
}
